<template>
  <div id="calendar_grid_container" class="calendar-container" :class="{ 'is-lead-plan': isLeadPlan }">
    <FullCalendar ref="fullCalendar" :key="calendarRenderKey" :options="fullcalendarOptions" :events="filteredTactics" />
    <ContextMenu ref="menu" :model="contextMenuItems" appendTo="body" />
    <OverlayPanel ref="planningItemSummaryOverlay" class="planning-item-summary-overlay" appendTo="body">
      <PlanningItemSummary :planningItemId="currentSummaryPlanningItemId" :instanceStartDate="currentSummaryInstanceStartDate" :instanceEndDate="currentSummaryInstanceEndDate" />
    </OverlayPanel>

    <div class="copyright-container">
      <p class="copyright">Copyright &copy; {{ currentYear }} Annum. All rights reserved. United States Patent Number
        12,079,779.</p>
    </div>

    <Dialog header="Distribute Tactic to Other Plans" :visible.sync="shouldShowDistributeTacticModal" :modal="true">
      <DistributeTacticModal :tacticId="rightClickedTacticId" @hide="shouldShowDistributeTacticModal = false" />
    </Dialog>

    <RecurrenceBreakModal
      :visible.sync="shouldShowRecurrenceBreakModal"
      context="calendar"
      @select="onRecurrenceBreakSelect"
      @cancel="onRecurrenceBreakCancel"
    />
  </div>
</template>

<script lang="ts">
import Vue from 'vue'
import { differenceInHours, isSameDay } from 'date-fns'
import { UserMostRecentView } from '@/models/User'
import { CalendarApi } from '@fullcalendar/vue'
import FullCalendar from '@fullcalendar/vue'
import dayGridPlugin from '@fullcalendar/daygrid'
import timeGridPlugin from '@fullcalendar/timegrid'
import rrulePlugin from '@fullcalendar/rrule'
import interactionPlugin from '@fullcalendar/interaction'
import Button from 'primevue/button'
import Dialog from 'primevue/dialog'
import OverlayPanel from 'primevue/overlaypanel'
import ID from '@/models/ID'
import Tactic from '@/models/Tactic'
import TacticPlatform from '@/models/TacticPlatform'
import { AlertMessageSeverity } from '@/models/AlertMessage'
import Subscription from '@/models/Subscription'
import ContextMenuItem from '@/models/ContextMenuItem'
import ContextMenu from 'primevue/contextmenu'
import Plan, { PlanType } from '@/models/Plan'
import Channel from '@/models/Channel'
// import {mapGetters} from 'vuex'
import PlanningItemSummary from '@/views/PlanningItemSummary.vue'
import Role from '@/models/Role'
import TacticType from '@/models/TacticType'
import RelatedInitiativeMapping from '@/models/RelatedInitiativeMapping'
import DistributeTacticModal from '@/components/DistributeTacticModal.vue'
import RecurrenceBreakModal from '@/components/RecurrenceBreakModal.vue'
import { RecurrenceBreakScope } from '@/models/RecurrenceBreakScope'
import TacticDisplayMixin from '@/mixins/TacticDisplayMixin'
import { getDtStart } from '@/utils/recurrence'

Vue.component('Button', Button)
Vue.component('ContextMenu', ContextMenu)
Vue.component('OverlayPanel', OverlayPanel)
Vue.component('Dialog', Dialog)

export default Vue.extend({
  name: 'CalendarGrid',
  mixins: [TacticDisplayMixin],
  components: {
    FullCalendar,
    PlanningItemSummary,
    DistributeTacticModal,
    RecurrenceBreakModal,
  },
  data() {
    return {
      fullCalendarApi: {} as CalendarApi,
      forceTacticsListUpdate: 0 as number,
      // Bumped to force a full FullCalendar remount (e.g. after a recurrence break, so the
      // updated rrule/EXDATE is re-parsed and stale drag state is discarded).
      calendarRenderKey: 0 as number,
      rightClickedTacticId: {} as ID,
      rightClickedTacticIsInitiative: false as boolean,
      rightClickedTacticIsInitiativeCustomization: false as boolean,
      rightClickedTacticIsLead: false as boolean,
      rightClickedTacticChannelIsNested: false as boolean,
      rightClickedTacticIsNested: false as boolean,
      currentSummaryPlanningItemId: {} as ID,
      currentSummaryInstanceStartDate: null as Date | null,
      currentSummaryInstanceEndDate: null as Date | null,
      currentSummaryTimeout: 0,
      isDisplayingSummaryPlanningItem: false as boolean,
      lastDateClickTimestamp: 0 as number,
      shouldShowDistributeTacticModal: false as boolean,
      shouldShowRecurrenceBreakModal: false as boolean,
      // Drag/drop context for an in-flight recurring-tactic break, awaiting the modal choice.
      pendingRecurrenceBreak: null as null | {
        revert: () => void
        seriesTactic: Tactic
        instanceStartDate: Date
        newStartDate: Date
        newEndDate: Date
      },
    }
  },
  computed: {
    // ...mapGetters(['events']),
    isLeadPlan(): boolean {
      return this.$store.getters.currentPlan.isLead
    },
    isNestedPlan(): boolean {
      return this.$store.getters.currentPlan.isNested
    },
    contextMenuItems(): ContextMenuItem[] {
      const returnArray = [] as ContextMenuItem[]
      if (this.shouldShowCopyContextMenuItem) {
        returnArray.push({
          label: 'Copy',
          icon: 'pi pi-fw pi-copy',
          command: this.openTacticDetailWithCopyId,
        })
      }
      if (this.shouldShowDistributeContextMenuItem) {
        returnArray.push({
          label: 'Distribute',
          icon: 'pi pi-fw pi-arrow-right',
          command: this.copyTacticToOtherPlans,
        })
      }
      return returnArray
    },
    shouldShowCopyContextMenuItem(): boolean {
      if (
        (this.isLeadPlan && !this.rightClickedTacticIsLead) ||
        (this.isNestedPlan && this.rightClickedTacticIsLead) ||
        this.rightClickedTacticIsInitiativeCustomization
      ) {
        return false
      }
      return true
    },
    shouldShowDistributeContextMenuItem(): boolean {
      return !this.rightClickedTacticIsInitiative && !this.rightClickedTacticIsInitiativeCustomization && !this.rightClickedTacticChannelIsNested && (this.isLeadPlan || this.isNestedPlan)
    },
    dateRange(): Date[] {
      return this.$store.getters.currentDateRange
    },
    fullcalendarOptions(): {} {
      return {
        plugins: [
          rrulePlugin,
          dayGridPlugin,
          timeGridPlugin,
          interactionPlugin,
        ],
        initialView: 'dayGridMonth',
        headerToolbar: {
          start: '',
          center: '',
          end: '',
        },
        editable: true,
        navLinks: true,
        events: this.filteredTactics,
        eventContent: (arg) => {
          return { html: this.getHtmlStringForEvent(arg.event) }
        },
        eventClick: (info) => {
          // Tactics can always be opened (in read-only mode when viewed across plans).
          if (
            info.event.extendedProps.internalId &&
            !info.event.extendedProps.isInitiative
          ) {
            // For recurring tactics, pass the clicked occurrence's date so TacticDetail knows
            // which instance is being edited (drives the This / This+following scope options).
            let instanceDate: Date | undefined
            if (info.event.extendedProps.repeats) {
              const clickedTactic: Tactic = this.$store.getters.currentPlan.tactics.find(
                (tactic) => tactic.id.intID == info.event.extendedProps.internalId.intID
              )
              instanceDate = clickedTactic
                ? this.resolveOccurrenceStartDate(clickedTactic, info.event.start)
                : info.event.start
            }
            this.showTacticById(info.event.extendedProps.internalId, instanceDate)
          }
        },
        eventMouseEnter: (info) => {
          if (info.event.classNames.includes('subscription')) {
            return
          }

          // Show planning item summary overlay with delay on first hover, no delay on subsequent hovers
          const planningItemSummaryDisplayDelay = this.isDisplayingSummaryPlanningItem ? 0 : 2000
          this.currentSummaryPlanningItemId = info.event.extendedProps.internalId
          if (info.event.extendedProps.repeats) {
            this.currentSummaryInstanceStartDate = info.event.start
            this.currentSummaryInstanceEndDate = info.event.end
          } else {
            this.currentSummaryInstanceStartDate = null
            this.currentSummaryInstanceEndDate = null
          }
          window.clearTimeout(this.currentSummaryTimeout)
          this.currentSummaryTimeout = window.setTimeout(() => {
            // The hover target is a raw DOM node captured at mouseenter time. If the calendar
            // re-renders (e.g. tactics list updates) before this delayed timeout fires, that
            // node can be detached from the document by the time we get here. PrimeVue's
            // OverlayPanel measures via getBoundingClientRect(), which returns all zeros for a
            // detached node, placing the overlay at the viewport's upper-left corner. Bail out
            // rather than show a mispositioned overlay.
            if (!document.contains(info.jsEvent.target as Node)) {
              return
            }
            this.isDisplayingSummaryPlanningItem = true
            if (this.$refs.planningItemSummaryOverlay) {
              // @ts-expect-error
              this.$refs.planningItemSummaryOverlay.show({ ...info.jsEvent, currentTarget: info.jsEvent.target })
            }
          }, planningItemSummaryDisplayDelay)
        },
        eventMouseLeave: (info) => {
          if (info.event.classNames.includes('subscription')) {
            return
          }

          // Hide planning item details overlay
          if (!this.$refs.planningItemSummaryOverlay) return
          (this.$refs.planningItemSummaryOverlay as Vue & { hide: () => boolean }).hide()

          // Reset timeout after delay
          window.clearTimeout(this.currentSummaryTimeout)
          this.currentSummaryTimeout = window.setTimeout(() => {
            this.isDisplayingSummaryPlanningItem = false
            this.currentSummaryPlanningItemId = {} as ID
            this.currentSummaryInstanceStartDate = null
            this.currentSummaryInstanceEndDate = null
            window.clearTimeout(this.currentSummaryTimeout)
          }, 3000)
        },
        eventDidMount: (info) => {
          if (info.event.extendedProps.internalId) {
            info.el.addEventListener(
              'contextmenu',
              (e) => {
                e.preventDefault()
                this.rightClickedTacticId = info.event.extendedProps.internalId
                this.rightClickedTacticIsInitiative = false
                this.rightClickedTacticIsInitiativeCustomization = false
                this.rightClickedTacticIsLead = info.event.extendedProps.isLead
                const channelForRightClickedTactic: Channel | undefined = this.$store.getters.currentPlan.channels.find((ch: Channel) => ch.tacticTypes.some((type: TacticType) => type.id.intID === info.event.extendedProps.tacticTypeId.intID))
                this.rightClickedTacticChannelIsNested = channelForRightClickedTactic?.isNested ?? false
                this.rightClickedTacticIsNested = info.event.extendedProps.isNested
                this.showContextMenu(e)
              },
              false
            )
          }
        },
        eventDragStart: () => {
          // A drag can begin while the hover summary overlay is still pending (its show-timeout
          // hasn't fired) or already open. FullCalendar captures the pointer during a drag, so
          // eventMouseLeave may never fire to dismiss it — leaving the overlay stranded. Its
          // hover anchor is gone, so PrimeVue renders it at 0,0 (the upper-left corner). Tear the
          // summary down immediately when a drag starts.
          this.hideSummaryOverlay()
        },
        eventResizeStart: () => {
          // Resizing captures the pointer the same way dragging does, so the summary overlay
          // can be left stranded the same way. See eventDragStart above.
          this.hideSummaryOverlay()
        },
        eventDrop: (info) => {
          this.onFullCalendarTacticDateUpdate(info)
        },
        eventResize: (info) => {
          this.onFullCalendarTacticDateUpdate(info)
        },
        eventAllow: (dropLocation, draggedEvent) => {
          // Find channel ID for draggedEvent
          const channelForTactic: Channel = this.$store.getters.currentPlan.channels.find((channel) => channel.tacticTypes.find((type) => type.id.intID === draggedEvent.extendedProps.tacticTypeId.intID))

          // Prevent dragging for contributors without channel permissions
          if (
            this.$store.getters.getPermissionLevelForPlanId(this.$store.getters.currentPlan.id) > Role.LEVEL_PLAN_ADMIN &&
            this.$store.getters.getUserRoleforPlanId(this.$store.getters.currentPlan.id).canEditAllChannels === false &&
            this.$store.getters.getUserRoleforPlanId(this.$store.getters.currentPlan.id).channels.filter((channelId) => channelId.intID === channelForTactic?.id.intID).length === 0
          ) {
            return false
          }
          // Prevent dragging for all viewers
          if (this.$store.getters.getPermissionLevelForPlanId(this.$store.getters.currentPlan.id) > Role.LEVEL_CONTRIBUTOR) {
            return false
          }
          // Prevent dragging of non-native tactics
          if (
            (this.isLeadPlan && draggedEvent.extendedProps.isLead) ||
            (!this.isLeadPlan && !draggedEvent.extendedProps.isLead)
          ) {
            return true
          } else {
            return false
          }
        },
        dateClick: (info) => {
          // Detect double click (two clicks within 500ms) to open TacticDetail with clicked day/time
          if (this.lastDateClickTimestamp !== 0 && (info.jsEvent.timeStamp - this.lastDateClickTimestamp < 500)) {
            this.lastDateClickTimestamp = 0
            // Month view dateStr is date-only, which TacticDetail reads as "no specific time" and
            // defaults to all-day. Only a time-grid slot click carries a time and creates a timed
            // tactic, so don't synthesize one here.
            this.$router.push({
              path: `/account/${this.$store.getters.currentAccount.id.intID}/plan/${this.$store.getters.currentPlan.id.intID}/tactic/0?datetime=${info.dateStr}`
            })
          } else {
            this.lastDateClickTimestamp = info.jsEvent.timeStamp
            window.setTimeout(() => {
              this.lastDateClickTimestamp = 0
            }, 500)
          }
        },
        navLinkDayClick: (event) => {
          this.$store.dispatch('updateDayViewDateRange', { newRange: [event, event] })
          this.$store.dispatch('updateCurrentDateRange', { newDateRange: [event, event] })

          this.$router.push({
            path: `/account/${this.$store.getters.currentAccount.id.intID}/plan/${this.$store.getters.currentPlan.id.intID}/calendar/day`,
          })
        },
      }
    },
    filteredTactics(): Tactic[] {
      this.forceTacticsListUpdate
      const sortedChannels = [...this.$store.getters.selectedChannels]
      const returnArray = [] as Tactic[]

      sortedChannels.sort((a, b) => {
        if (a.orderIndex === b.orderIndex) {
          return 0
        }
        return a.orderIndex > b.orderIndex ? 1 : -1
      }).forEach((channel) => {
        channel.tacticTypes.forEach((type) => {
          if (
            this.$store.getters.selectedTacticTypeIdSet.has(type.id.intID) &&
            type.name.toLowerCase() !== this.$store.getters.monthlyFocusTypeName
          ) {
            //Only show "Monthly (Channel) Focus" tactics in masthead
            type.tactics.forEach((tactic) => {
              //Ensure tactic belongs on grid and not masthead
              const isLongTactic = (this as any).isTacticLongerThanThreshold(tactic, 21)
              if (
                !(this as any).isTacticInFilteredOutPlan(tactic) &&
                !(this as any).isTacticRelatedToDeselectedTag(tactic) &&
                !(this as any).isTacticRelatedToDeselectedPlatform(tactic) &&
                !(this as any).isTacticRelatedToDeselectedInitiative(tactic) &&
                (!isLongTactic || (isLongTactic && !(this as any).isTacticInPaidChannel(tactic)))
              ) {
                //FullCalendar uses id property, so copy id value for internal use
                tactic.internalId = tactic.id
                //Defense-in-depth: an empty-string rrule is invalid and makes FullCalendar's
                //rrule plugin throw "Invalid empty string" on mount, crashing the whole calendar.
                //Normalize it to null (non-recurring) so one bad event can't take down the view.
                if (tactic.rrule === '') tactic.rrule = null
                //Add classes for channel name and paid-media
                const tacticClassNames = [
                  tactic.channelName.toLowerCase().replace(' ', '-'),
                ]
                if (this.$store.getters.paidChannelNamesSet.has(tactic.channelName.toLowerCase())) {
                  tacticClassNames.push('paid-media')
                }
                if (tactic.isNested) {
                  tacticClassNames.push('is-nested')
                }
                if (tactic.isLead) {
                  tacticClassNames.push('is-lead')
                }
                if (type.shouldHideTacticsFromNestedPlans) {
                  tacticClassNames.push('is-hidden-from-nested-plans')
                }
                tactic.classNames = tacticClassNames

                //Apply custom color from Channel.uiColor if available
                if (channel.uiColor) {
                  tactic.backgroundColor = channel.uiColor
                }

                //Add to return array for display on calendar
                returnArray.push(tactic)
              }
            })
          }
        })
      })

      const isDayView = this.$route.name === 'CalendarDay'
      this.$store.getters.selectedSubscriptions.forEach((subscription) => {
        subscription.events.forEach((tactic) => {
          if (
            !(this as any).isTacticLongerThanThreshold(tactic, 21) &&
            !this.isEventDuplicate(tactic, subscription)
          ) {
            tactic.editable = false
            tactic.classNames = ['subscription']
            if (isDayView) {
              const allDayTactic = Object.create(tactic) as Tactic
              allDayTactic.isAllDay = true
              returnArray.push(allDayTactic)
            } else {
              returnArray.push(tactic)
            }
          }
        })
      })
      return returnArray
    },
    isGlobalNavCollapsed(): boolean {
      return this.$store.getters.isGlobalNavCollapsed
    },
    isPrintDialogOpen(): boolean {
      return this.$store.getters.isPrintDialogOpen
    },
    currentYear(): number {
      return new Date().getFullYear()
    },
  },
  mounted: function () {
    // Set date range to month range
    if (this.$route.name == 'CalendarDay') {
      this.$store.dispatch('updateCurrentDateRange', { newDateRange: this.$store.getters.dayViewDateRange, shouldFetchTactics: false })
    } else {
      this.$store.dispatch('updateCurrentDateRange', { newDateRange: this.$store.getters.monthViewDateRange, shouldFetchTactics: false })
    }

    // Set up FullCalendar
    this.fullCalendarApi = (this.$refs.fullCalendar as any).getApi()
    this.checkForDayView()
    this.fullCalendarApi.gotoDate(this.dateRange[0])
    this.fullCalendarApi.setOption('height', 'auto')
    this.fullCalendarApi.setOption('fixedWeekCount', false)
    this.fullCalendarApi.setOption('eventOrder', '-isSubscriptionEvent')

    // Update date range to cover entire month of start date
    this.$store.dispatch('updateCurrentDateRange', {
      newDateRange: [
        this.dateRange[0],
        new Date(
          this.dateRange[0].getFullYear(),
          this.dateRange[0].getMonth() + 1,
          0
        ),
      ],
      shouldFetchTactics: true
    })
  },
  beforeDestroy() {
    window.clearTimeout(this.currentSummaryTimeout)
  },
  watch: {
    dateRange: function () {
      this.fullCalendarApi.gotoDate(this.dateRange[0])
      this.forceTacticsListUpdate++
    },
    isGlobalNavCollapsed: function () {
      window.setTimeout(() => {
        this.fullCalendarApi.updateSize()
      }, 301)
    },
    isPrintDialogOpen: function () {
      const allTacticEls = document.querySelectorAll('.fc-daygrid-event-harness-abs')
      const containerEl = document.querySelector('.fc-daygrid-day')
      const containerWidth = containerEl ? containerEl.getBoundingClientRect().width : 0

      if (this.isPrintDialogOpen) {
        // Restore right pixel values for each tactic after print
        for (const element of allTacticEls) {
          (element as HTMLElement).style.right = (element as HTMLElement).dataset.right as string;
        }
      }

      window.setTimeout(() => {
        // Re-render calendar elements
        this.fullCalendarApi.updateSize()

        if (this.isPrintDialogOpen) {
          // Stash right pixel value in data attribute for each tactic and use percentage for printing before print
          for (const element of allTacticEls) {
            const thisRightString = (element as HTMLElement).style.right;
            const thisRightNumber = thisRightString ? parseFloat(thisRightString.replace('px', '')) : 0;
            (element as HTMLElement).dataset.right = thisRightString ? thisRightString : '0px';
            (element as HTMLElement).style.right = ((thisRightNumber / containerWidth) * 100) + '%';
          }
        }
      }, this.isPrintDialogOpen ? 10 : 301)
    },
    $route: function (to, from) {
      if (to.path.indexOf('calendar') > -1) {
        //Force re-render of calendar by forcing update of tactics list when returning from TacticDetail
        this.forceTacticsListUpdate++
      }
      this.checkForDayView()
    },
  },
  methods: {
    // Immediately dismiss the hover summary overlay and cancel any pending show-timeout /
    // hover state. Used when a drag starts (see eventDragStart) so the overlay can't render
    // stranded in the corner while the pointer is captured for the drag.
    hideSummaryOverlay(): void {
      window.clearTimeout(this.currentSummaryTimeout)
      this.isDisplayingSummaryPlanningItem = false
      this.currentSummaryPlanningItemId = {} as ID
      this.currentSummaryInstanceStartDate = null
      this.currentSummaryInstanceEndDate = null
      if (this.$refs.planningItemSummaryOverlay) {
        (this.$refs.planningItemSummaryOverlay as Vue & { hide: () => boolean }).hide()
      }
    },
    checkForDayView() {
      if (this.$route.path.includes('/day')) {
        this.fullCalendarApi.changeView('timeGridDay')
      }
    },
    isEventDuplicate(event: Tactic, subscription: Subscription): boolean {
      let returnValue = false
      const duplicateMappingsForTactic = subscription.duplicateEventsMap.filter(
        (duplicateMapping) => duplicateMapping.eventId == event.id.intID
      )
      // Only check for duplicates if there is a duplicate for the provided event
      if (duplicateMappingsForTactic.length) {
        duplicateMappingsForTactic.forEach((duplicateMapping) => {
          // Check if subscription with duplicate is visible
          const selectedSubscriptionWithDuplicate =
            this.$store.getters.selectedSubscriptions.find(
              (selectedSubscription) =>
                selectedSubscription.id.intID ==
                duplicateMapping.duplicateSubscriptionId
            )
          if (selectedSubscriptionWithDuplicate) {
            // Check if subscription with duplicate has a lower ID value than the provided subscription - event with lowest subscription ID will be shown
            if (
              duplicateMapping.duplicateSubscriptionId <
              duplicateMapping.subscriptionId
            ) {
              returnValue = true
            }
          }
        })
      }
      return returnValue
    },
    // FullCalendar zeroes an all-day recurring occurrence's time-of-day to local midnight when
    // expanding its rrule (@fullcalendar/common's expandRecurringRanges calls startOfDay()) —
    // but this app's all-day tactics store their series DTSTART at a non-midnight time. Using
    // FullCalendar's date as-is for an all-day tactic's occurrence therefore never matches the
    // real DTSTART wall-clock time, so an EXDATE built from it (see utils/recurrence.ts) silently
    // fails to exclude the intended occurrence. Restore the tactic's true time-of-day, keeping
    // the calendar day FullCalendar reported.
    //
    // The time-of-day must come from the series' own persisted DTSTART (via getDtStart), NOT from
    // tactic.startDate: that field is independently normalized for display (see parseAllDayDate,
    // which anchors it to local noon) and is no longer guaranteed to match whatever wall-clock
    // time was actually baked into the rrule when the recurrence was created.
    resolveOccurrenceStartDate(tactic: Tactic, fullCalendarDate: Date): Date {
      const resolved = new Date(fullCalendarDate)
      if (tactic.isAllDay) {
        const dtStart = tactic.rrule ? getDtStart(tactic.rrule) : null
        const timeSource = dtStart ?? tactic.startDate
        resolved.setHours(
          timeSource.getHours(),
          timeSource.getMinutes(),
          timeSource.getSeconds(),
          timeSource.getMilliseconds()
        )
      }
      return resolved
    },
    onFullCalendarTacticDateUpdate(info) {
      const thisTactic: Tactic = this.$store.getters.currentPlan.tactics.find(
        (tactic) => tactic.id.intID == info.event.extendedProps.internalId.intID
      )
      if (!thisTactic) {
        return
      }

      //Calculate new start/end dates from the drop
      const newStartDate = new Date(info.event.start)
      let newEndDate = new Date(info.event.end)
      if (thisTactic.isAllDay) {
        //All day
        if (
          thisTactic.startDate.getFullYear() ==
          thisTactic.endDate.getFullYear() &&
          thisTactic.startDate.getMonth() == thisTactic.endDate.getMonth() &&
          thisTactic.startDate.getDate() == thisTactic.endDate.getDate()
        ) {
          newEndDate = new Date(info.event.start)
        } else {
          newEndDate.setMinutes(newEndDate.getMinutes() - 1)
        }
      } else {
        if (
          thisTactic.startDate.getFullYear() ==
          thisTactic.endDate.getFullYear() &&
          thisTactic.startDate.getMonth() == thisTactic.endDate.getMonth() &&
          thisTactic.startDate.getDate() == thisTactic.endDate.getDate()
        ) {
          //Specific time
          newEndDate = new Date(info.event.start)
        } else {
          //Multi-day with specific time
          newEndDate = new Date(
            newStartDate.getTime() +
            (thisTactic.endDate.getTime() - thisTactic.startDate.getTime())
          )
        }
      }

      if (thisTactic.isAllDay) {
        // FullCalendar represents all-day event dates as local midnight, which has
        // zero margin against timezone-driven day rollover once serialized to UTC
        // on save. Re-anchor to local noon, consistent with TacticDetail.vue/
        // InitiativeDetail.vue's normalizeAllDayDates().
        newStartDate.setHours(12, 0, 0, 0)
        newEndDate.setHours(12, 0, 0, 0)
      }

      // Recurring tactics: a drag is a breaking change — defer to the modal to choose scope.
      // info.oldEvent.start is the dragged occurrence's ORIGINAL series datetime (the EXDATE /
      // split point); newStartDate/newEndDate are where it was dropped.
      if (info.event.extendedProps.repeats) {
        this.pendingRecurrenceBreak = {
          revert: () => info.revert(),
          seriesTactic: thisTactic,
          instanceStartDate: this.resolveOccurrenceStartDate(thisTactic, info.oldEvent.start),
          newStartDate,
          newEndDate,
        }
        this.shouldShowRecurrenceBreakModal = true
        return
      }

      //Set new dates
      thisTactic.startDate = newStartDate
      thisTactic.endDate = newEndDate

      //Persist date updates
      this.$store.getters.services.tactics.update(thisTactic).then(
        (updatedTactic) => {
          Vue.prototype.$toast.add({
            severity: AlertMessageSeverity.success,
            summary: 'Date updated.',
            life: 3000,
          })
        },
        (error) => {
          Vue.prototype.$toast.add({
            severity: AlertMessageSeverity.error,
            summary: 'Error updating date.',
            detail: error,
          })
        }
      )
    },
    onRecurrenceBreakSelect(scope: RecurrenceBreakScope) {
      const pending = this.pendingRecurrenceBreak
      if (!pending) {
        return
      }

      const options = {
        seriesTactic: pending.seriesTactic,
        instanceStartDate: pending.instanceStartDate,
        newStartDate: pending.newStartDate,
        newEndDate: pending.newEndDate,
      }

      let breakPromise: Promise<Tactic | null>
      if (scope === RecurrenceBreakScope.ThisInstance) {
        breakPromise = (this as any).breakRecurringTacticThisInstance(options)
      } else if (scope === RecurrenceBreakScope.ThisAndFollowing) {
        breakPromise = (this as any).breakRecurringTacticThisAndFollowing(options)
      } else {
        // "All" isn't offered on the calendar — treat anything else as a cancel.
        this.onRecurrenceBreakCancel()
        return
      }

      breakPromise.then((result) => {
        if (result) {
          // Remount the calendar so FullCalendar re-parses the now-EXDATE'd recurrence and
          // drops the dragged occurrence's transient state. A prop/options update alone does
          // not force rrule re-expansion, so the old instance would otherwise linger until a
          // page refresh.
          this.forceCalendarRerender()
        } else {
          // Break failed — undo the visual drag; the data is unchanged.
          pending.revert()
        }
        this.pendingRecurrenceBreak = null
      })
    },
    onRecurrenceBreakCancel() {
      if (this.pendingRecurrenceBreak) {
        this.pendingRecurrenceBreak.revert()
        this.pendingRecurrenceBreak = null
      }
    },
    forceCalendarRerender() {
      // Remount <FullCalendar> via :key, then re-acquire its API and restore view state
      // (mirrors the FullCalendar setup in mounted()).
      this.calendarRenderKey++
      this.$nextTick(() => {
        this.fullCalendarApi = (this.$refs.fullCalendar as any).getApi()
        this.checkForDayView()
        this.fullCalendarApi.gotoDate(this.dateRange[0])
        this.fullCalendarApi.setOption('height', 'auto')
        this.fullCalendarApi.setOption('fixedWeekCount', false)
        this.fullCalendarApi.setOption('eventOrder', '-isSubscriptionEvent')
      })
    },
    showTacticById(id: ID, instanceDate?: Date) {
      let path = `/account/${this.$store.getters.currentAccount.id.intID}/plan/${this.$route.params.planId}/tactic/${id.intID}`
      if (instanceDate) {
        path += `?instanceDate=${instanceDate.toISOString()}`
      }
      this.$router.push({ path })
    },
    showContextMenu(e: MouseEvent) {
      if (this.contextMenuItems.length) {
        (this.$refs.menu as Vue & { show: (e) => boolean }).show(e)
      }
    },
    openTacticDetailWithCopyId() {
      this.$router.push({
        path:
          `/account/${this.$store.getters.currentAccount.id.intID}/plan/${this.$route.params.planId}/tactic/0?copyof=${this.rightClickedTacticId.intID}`
      })
    },
    copyTacticToOtherPlans() {
      this.shouldShowDistributeTacticModal = true
    },
    getHtmlStringForEvent(event): string {
      let htmlString = '<div class="tactic-item">'
      if (event.backgroundColor !== '') {
        htmlString += '<div class="bkg" style="background-color: ' + event.backgroundColor + ';" id="event-' + event.extendedProps.internalId.intID + '"></div>'
      } else {
        htmlString += '<div class="bkg"></div>'
      }
      htmlString +=
        '<span class="title">' + this.getDisplayTitleForEvent(event) + '</span>'
      htmlString += '</div>'
      return htmlString
    },
    getDisplayTitleForEvent(event): string {
      if (event.extendedProps.isSubscriptionEvent) {
        //Subscription Events
        return event.title
      }
      //Tactics
      let prefix = event.extendedProps.channelName
      const suffix = event.extendedProps.tacticTypeName
      let tacticTitle = event.title
      // Replace channel name with platform name if available
      if (event.extendedProps.tacticPlatforms && event.extendedProps.tacticPlatforms.length) {
        prefix = ''
        event.extendedProps.tacticPlatforms.forEach((platformOnTactic) => {
          prefix += this.$store.getters.currentTacticPlatforms.find(
            (platform) => platform.id.intID === platformOnTactic.id.intID
          )?.name + ' '
        })
      }
      // Prefix tactic title with plan name if nested
      if (
        this.isLeadPlan &&
        event.extendedProps.isNested &&
        event.extendedProps.abbreviatedPlanName !== ''
      ) {
        tacticTitle =
          event.extendedProps.abbreviatedPlanName + '  |  ' + tacticTitle
      }
      return (
        '<span class="meta">' +
        prefix +
        ' &bull; ' +
        suffix +
        '</span> ' +
        tacticTitle
      )
    },
  },
})
</script>

<style lang="scss">
@import '@/styles/_imports.scss';

.calendar-container {
  --fc-event-bg-color: #000;
  --fc-event-border-color: #000;
  --fc-event-text-color: #0a0a0a;
  --fc-today-bg-color: #efefef;
  --fc-border-color: #efefef;

  .fc-scroller {
    overflow: visible !important;
  }

  .fc-col-header,
  .fc-daygrid-body,
  .fc-timegrid-body,
  .fc-scrollgrid-section-body table,
  .fc-scrollgrid-section-footer table {
    width: 100% !important;
  }

  .fc-direction-ltr .fc-timegrid-all-day .fc-daygrid-event.fc-event-start,
  .fc-direction-rtl .fc-timegrid-all-day .fc-daygrid-event.fc-event-end {
    margin-left: 0;
  }

  .fc-direction-ltr .fc-timegrid-all-day .fc-daygrid-event.fc-event-end,
  .fc-direction-rtl .fc-timegrid-all-day .fc-daygrid-event.fc-event-start {
    margin-right: 0;
  }

  .fc-daygrid-day-frame {
    min-height: 10rem;
  }

  .fc-daygrid-day-events {
    .fc-daygrid-event.fc-event-start, 
    .fc-daygrid-event.fc-event-end {
      margin-left: 0;
      margin-right: 0;
    }
  }

  .fc-day-sat,
  .fc-day-sun {
    &:not(.fc-col-header-cell) {
      background-color: #fcfcfc;
    }
  }

  .fc .fc-view-harness {
    th.fc-col-header-cell {
      padding: 0.4rem;
      border-left-color: #efefef;
      border-right-color: #efefef;
      background-color: #efefef;
      font-size: 1rem;
      line-height: 1.6rem;
      border: 0;
    }

    .fc-scrollgrid,
    th,
    td {
      border: 1px solid var(--fc-border-color, #ddd);
    }

    table.fc-scrollgrid {
      border-width: 0;
    }

    .fc-event,
    .fc-event-main {
      color: #0a0a0a !important;
      background-color: transparent;
      border: 0;
    }

    .fc-daygrid,
    .fc-timegrid {
      .fc-daygrid-day-bg {
        opacity: 0.1;
      }

      .fc-event {
        background-color: transparent !important;

        .tactic-item {
          display: flex;
          align-items: center;
          min-height: 2.8rem;
          padding: 0.4rem 0.6rem;
          border-radius: 0;
          border: none;
          box-shadow: none;
          background-color: transparent;
          color: #0a0a0a;
          white-space: unset;
          font-size: 0.9rem;
          line-height: 1rem;
          overflow: hidden;
          cursor: pointer;

          .bkg {
            content: '';
            display: block;
            position: absolute;
            top: 0;
            right: 0;
            bottom: 0;
            left: 0;
            background-color: $tacticColorDefaultBkg;
            z-index: -1;
            opacity: $nestedBkgColorOffset;
          }

          .title {
            white-space: normal;
          }
        }

        &.subscription {
          cursor: default;
          box-shadow: inset 0 0 0 0.1rem $tacticColorSubscription;
          border-radius: 0;

          .tactic-item {
            cursor: default;
          }

          .bkg {
            background-color: $tacticColorSubscriptionBkg;
          }
        }

        &.initiatives {
          border-color: $tacticColorInitiatives;

          .bkg {
            background-color: $tacticColorInitiativesBkg;
          }
        }

        &.website {
          border-color: $tacticColorWebsite;

          .bkg {
            background-color: $tacticColorWebsiteBkg;
          }
        }

        &.email {
          border-color: $tacticColorEmail;

          .bkg {
            background-color: $tacticColorEmailBkg;
          }
        }

        &.social-media {
          border-color: $tacticColorSocialMedia;

          .bkg {
            background-color: $tacticColorSocialMediaBkg;
          }
        }

        &.collateral {
          border-color: $tacticColorCollateral;

          .bkg {
            background-color: $tacticColorCollateralBkg;
          }
        }

        &.direct-mail {
          border-color: $tacticColorDirectMail;

          .bkg {
            background-color: $tacticColorDirectMailBkg;
          }
        }

        &.pr {
          border-color: $tacticColorPR;

          .bkg {
            background-color: $tacticColorPRBkg;
          }
        }

        &.advertising-assets {
          border-color: $tacticColorAdvertisingAssets;

          .tactic-item:before {
            background-color: $tacticColorAdvertisingAssetsBkg;
          }
        }

        &.signage {
          border-color: $tacticColorSignage;

          .bkg {
            background-color: $tacticColorSignageBkg;
          }
        }

        &.paid-media {
          border-color: $tacticColorPaidMedia;

          .bkg {
            background-color: $tacticColorPaidMediaBkg;
          }
        }

        &.is-lead {
          .tactic-item {
            flex-direction: row-reverse;
            justify-content: flex-end;
            column-gap: 1rem;
          }

          &:not(.is-hidden-from-nested-plans) {
            .tactic-item::after {
              content: '\e925'; // pi-home
              display: block;
              font-family: 'primeicons';
              font-size: 1.6rem;
              speak: none;
              font-style: normal;
              font-weight: normal;
              font-variant: normal;
              text-transform: none;
            }
          }

          .bkg {
            opacity: 1;
          }
        }

        .meta {
          display: block;
          margin-bottom: 0.2rem;
          font-size: 0.7rem;
          line-height: 0.9rem;
        }

        .social-icon {
          display: inline-block;
          width: 0.75rem;
          height: 0.75rem;
          margin-right: 0.25rem;
          color: #fff;
          border: 0.05rem solid #fff;
          border-radius: 50%;

          &.icon-facebook {
            background-color: #1877f2;
          }

          &.icon-instagram {
            background-color: #c32aa3;
          }

          &.icon-pinterest {
            background-color: #bd081c;
          }

          &.icon-linkedin {
            background-color: #007bb5;
          }

          &.icon-twitter {
            background-color: #1da1f2;
          }

          &.icon-snapchat {
            background-color: #fffc00;
          }

          &.icon-whatsapp {
            background-color: #25d366;
          }

          &.icon-tumblr {
            background-color: #35465d;
          }

          &.icon-reddit {
            background-color: #ff4500;
          }

          &.icon-tiktok {
            background-color: #010101;
          }
        }
      }
    }
  }
}
</style>